export const reIntStr = "^\\d*?$";
export const reFloatStr = "^\\d+?(?:\\.\\d*?)?$";
export const reMoneyStr = "^\\d+?(?:\\.\\d{0,2})?$";

export const reTrue = /^true$/i;
export const reFalse = /^false$/i;
export const reUndefined = /^undefined$/i;
export const reInt = new RegExp(reIntStr);
export const reFloat = new RegExp(reFloatStr);
export const reMoney = new RegExp(reMoneyStr);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isString = (value: any): boolean => {
  return typeof value === "string";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isFunction = (value: any): boolean => {
  return (
    value &&
    (typeof value === "function" ||
      value instanceof Function ||
      toString.call(value) === "[object Function]")
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObject = (value: any): boolean => {
  return value && toString.call(value) === "[object Object]";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isDate = (value: any): boolean => {
  return (
    value &&
    (value instanceof Date || toString.call(value) === "[object Date]") &&
    value.toString() !== "Invalid Date"
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNumber = (value?: any): boolean => {
  return typeof value === "number" && isFinite(value);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNumeric = (value?: any): boolean => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isBoolean = (value?: any): boolean => {
  return typeof value === "boolean";
};

export const stringToIntArray = (str?: string, separator: string = ",") => {
  if (!isNonEmptyString(str)) return undefined;

  const strInts = str?.split(separator);

  if (!Array.isArray(strInts)) return undefined;

  const result = [];

  for (let i = 0, l = strInts.length; i < l; ++i) {
    let valueStr = strInts[i];

    try {
      const value = parseInt(valueStr, 10);

      if (!isNaN(value)) result.push(value);
    } catch {
      continue;
    }
  }

  return result.length ? result : undefined;
};

export const isDigit = (str: string) => !/\D/.test(str);

export const isAlphanumeric = (str: string) => !/[^0-9a-z]/i.test(str);

export const isCode128 = (str: string) => !/[^0-9a-z &-]/i.test(str);

export const tryParseInt = (str?: string) => {
  if (!str) return NaN;

  let result;
  try {
    result = parseInt(str, 10);
  } catch (e) {
    result = NaN;
  }

  return result;
};

export const isStringEqual = (
  left?: string,
  right?: string,
  caseInsensitive?: boolean
): boolean =>
  (left == null && right == null) ||
  (isString(left) &&
    isString(right) &&
    (caseInsensitive ? left!.trim().toLowerCase() : left!.trim()) ===
      (caseInsensitive ? right!.trim().toLowerCase() : right!.trim()));

export const isStringEqualCI = (left?: string, right?: string): boolean =>
  isStringEqual(left, right, true);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNonEmptyNumber = (value?: any): boolean =>
  isNumber(value) && value > 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNonEmptyString = (str?: any): boolean =>
  isString(str) && str!.trim().length > 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNonEmptyArray = (arr?: any[]): boolean =>
  Array.isArray(arr) && arr.length > 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNonEmptyObject = (o?: any): boolean =>
  isObject(o) && Object.keys(o).length > 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBoolean = (value?: any): boolean => {
  return isBoolean(value)
    ? value
    : isNumber(value)
    ? value !== 0
    : isString(value)
    ? reTrue.test(value)
    : false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getInt = (value?: any, radix: number = 10): number | undefined => {
  if (isNumber(value)) return value;

  let result;

  try {
    result = parseInt(value, radix);
  } catch (error) {
    result = undefined;
  }

  return !isNaN(result as number) ? result : undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFloat = (value?: any): number | undefined => {
  if (isNumber(value)) return value;

  let result;

  try {
    result = parseFloat(value);
  } catch (error) {
    result = undefined;
  }

  return !isNaN(result as number) ? result : undefined;
};

export const intersect = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  left?: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  right?: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareFn?: (leftItem: any, rightItem: any) => boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] =>
  Array.isArray(left) && Array.isArray(right)
    ? left.filter(
        (leftItem) =>
          (isFunction(compareFn)
            ? right.findIndex((rightItem) => compareFn?.(leftItem, rightItem))
            : right.indexOf(leftItem)) !== -1
      )
    : [];

export const except = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  left?: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  right?: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareFn?: (leftItem: any, rightItem: any) => boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] =>
  Array.isArray(left) && Array.isArray(right)
    ? left.filter(
        (leftItem) =>
          (isFunction(compareFn)
            ? right.findIndex((rightItem) => compareFn?.(leftItem, rightItem))
            : right.indexOf(leftItem)) === -1
      )
    : Array.isArray(left)
    ? left
    : [];

export const unique = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arr?: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareFn?: (leftItem: any, rightItem: any) => boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] =>
  Array.isArray(arr)
    ? arr.filter((item, index, array) =>
        isFunction(compareFn)
          ? array.findIndex((_item_) => compareFn?.(item, _item_)) === index
          : array.indexOf(item) === index
      )
    : [];

export const isStringCommaSeparatedIntegers = (value?: string) => {
  if (!isNonEmptyString(value)) {
    return false;
  }

  const items = value!.split(",").map((item) => item.trim());

  return items.every((item) => {
    return reInt.test(item);
  });
};

export const combinePaths = (base?: string, path?: string) =>
  isNonEmptyString(base) && isNonEmptyString(path)
    ? base!.replace(/\/$/, "") + "/" + path!.replace(/^\//, "")
    : isNonEmptyString(base)
    ? base
    : undefined;

export const addTimestampToUrl = (url?: string) =>
  isNonEmptyString(url)
    ? `${url}${
        url?.indexOf("?") !== -1 ? "&" : "?"
      }timestamp=${new Date().valueOf()}`
    : undefined;
